import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { View } from 'modules/shift_management/lib/types'
import type { ViewDimension } from 'modules/shift_management/store/internal/models/View'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'

export interface Column {
  date: moment.Moment
  formattedDate: string
  headerCell: string
  minWidth: ViewDimension
  // These fields are mainly used by the daily view
  isNextPeriod?: boolean
  hour: number
}

export type ColumnsType = Column[]

const formatHeader = (date: moment.Moment, view: View) => {
  if (view.isDaily) return `${date.format('HH')}h`

  const shortDate = dateToString(date, 'ui_short')

  if (view.isWeekly) return shortDate

  return shortDate[0] + date.format('DD')
}

const buildExtraDates = (view: View, columnsToAdd = 0) => {
  const dates: moment.Moment[] = []
  const firstDate = view.lastDay.clone().add(1, view.columnUnit).startOf('hour')
  const lastDate = firstDate.clone().add(columnsToAdd, view.columnUnit)
  let date = firstDate

  while (date < lastDate) {
    dates.push(date)
    date = date.clone().add(1, view.columnUnit)
  }

  return dates
}

export const buildColumns = (view: View, columnsToAdd = 0): ColumnsType => {
  const dates = view.dates.concat(buildExtraDates(view, columnsToAdd))

  const dateCells: ColumnsType = dates.map((date: moment.Moment) => {
    const headerCell = formatHeader(date, view)
    const isNextPeriod = date.isAfter(view.lastDay)
    const formattedHour = Number(date.format('HH'))
    const hour = isNextPeriod ? formattedHour + 24 : formattedHour

    return {
      date,
      formattedDate: date.format('YYYY-MM-DD'),
      headerCell,
      minWidth: view.columnMinWidth,
      isNextPeriod,
      hour,
    }
  })

  const firstHeaderCell = view.isByAreas
    ? i18n.t('shift_management.view_by_area.header_cell')
    : i18n.t('common.employee')

  return [
    {
      date: moment(),
      formattedDate: moment().format('YYYY-MM-DD'),
      headerCell: firstHeaderCell,
      minWidth: 's240',
      hour: 0,
    },
    ...dateCells,
  ]
}

type GridType = 'header' | 'content'

export const gridTemplateColumns = (
  firstCellWidth: ViewDimension,
  view: View,
  gridType: GridType,
  columnsToAdd = 0
) => {
  const cellNumber = view.dates.length + columnsToAdd
  const columnMinWidth = view.columnMinWidth
  const dimensions = overrideGammaDimensions()
  const firstCell = dimensions[firstCellWidth]
  const minWidth = dimensions[columnMinWidth]
  const isContent = gridType === 'content'
  const width = isContent && view.isDaily ? `calc(${minWidth} * ${cellNumber})` : minWidth
  const repeat = isContent && view.isDaily ? 1 : cellNumber

  return {
    gridTemplateColumns: `minmax(${firstCell}, ${firstCell}) repeat(${repeat}, minmax(${width}, 1fr))`,
  }
}

export const dailyGridTemplateColumns = (view: View, columnsToAdd = 0) => {
  const cellNumber = view.dates.length + columnsToAdd
  const columnMinWidth = view.columnMinWidth
  const dimensions = overrideGammaDimensions()
  const minWidth = dimensions[columnMinWidth]

  return { gridTemplateColumns: `repeat(${cellNumber}, minmax(${minWidth}, 1fr))` }
}

export const gridColumn = (start: number, end: number) => {
  return {
    gridColumnStart: `calc(${start} + 1)`,
    gridColumnEnd: `calc(${end} + 1)`,
  }
}
